import { Injectable, WritableSignal, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { GlobalsService } from 'src/app/services/globals.service';
import { CarFormData, CarValuationRequest, CarValuationResponse, UploadPhotoRequest, UploadPhotoResponse, GetUserIPResponse } from '@models/car-valuation.model';
import { HotToastService } from '@ngxpert/hot-toast';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class CarValuationService {
  private http = inject(HttpClient);
  private globalsService = inject(GlobalsService);
  private hotToastService = inject(HotToastService);
  private translateService = inject(TranslateService);
  public formId: WritableSignal<string | null> = signal(null);
  public carValuationData: WritableSignal<CarFormData> = signal({} as CarFormData);
  public step: WritableSignal<number> = signal(1);
  public totalSteps: WritableSignal<number> = signal(5);
  public done: WritableSignal<boolean> = signal(false);

  submitCarValuation(request: CarValuationRequest): Observable<CarValuationResponse> {
    return this.http.post<CarValuationResponse>(`${environment.apiBeta74}/v1/forms/car-valuation/`, request, { headers: this.globalsService.headerHashLogged() });
  }

  uploadPhoto(data: UploadPhotoRequest): Observable<UploadPhotoResponse> {
    return this.http.post<UploadPhotoResponse>(`${environment.apiBeta822}/v2/frontend/content/file/upload`, data, { headers: this.globalsService.headerHash() }).pipe(
      this.hotToastService.observe({
        loading: this.translateService.instant('forms.hotToast.uploadPhoto.loading'),
        success: this.translateService.instant('forms.hotToast.uploadPhoto.success'),
        error: this.translateService.instant('forms.hotToast.uploadPhoto.error'),
      }),
    );
  }

  getUserIp(): Observable<GetUserIPResponse> {
    return this.http.get<GetUserIPResponse>('https://api.ipify.org/?format=json');
  }

  generateFormMessage(): string {
    const data = this.carValuationData();
    let message = '';

    const toBoolText = (val: any): string => {
      if (val === undefined || val === null) return '';
      if (typeof val === 'boolean') return val ? 'Ano' : 'Ne';
      return val === '1' ? 'Ano' : 'Ne';
    };

    if (data.specificationCar) {
      const spec = data.specificationCar;

      message += 'SPECIFIKACE VOZIDLA:\n';

      message += `Převodovka: ${spec.gearbox}, `;
      message += `Karoserie: ${spec.bodyType}, `;
      message += `Objem motoru: ${spec.engineVolume}, `;
      message += `Výkon: ${spec.enginePerformance} kW, `;

      if (spec.driveType) {
        const driveType = spec.driveType === '1' ? 'Přední' : spec.driveType === '2' ? 'Zadní' : '4x4';
        message += `Pohon: ${driveType}, `;
      }

      if (spec.seats) message += `Počet sedadel: ${spec.seats}, `;
      if (spec.newCS) message += `Nové v ČR: ${toBoolText(spec.newCS)}, `;
      if (spec.countryType) message += `Země původu: ${spec.countryType}, `;
      if (spec.ownerNumber) message += `Počet majitelů: ${spec.ownerNumber}, `;
      if (spec.dph !== undefined) message += `Odpočet DPH: ${toBoolText(spec.dph)}, `;
      if (spec.serviceBook) message += `Servisní knížka: ${toBoolText(spec.serviceBook)}, `;
      if (spec.warranty) message += `Záruka: ${toBoolText(spec.warranty)}, `;

      if (spec.airCondition) {
        message += `Klimatizace: ${toBoolText(spec.airCondition)}, `;
        if (spec.airCondition === '1' && spec.airConditionType) {
          message += `Typ klimatizace: ${spec.airConditionType === '1' ? 'Automatická' : 'Manuální'}, `;
        }
      }

      message = message.replace(/,\s$/, '\n\n');
    }

    if (data.equipment) {
      const eq = data.equipment;
      message += 'VÝBAVA VOZIDLA:\n';

      if (eq.equipmentName) message += `Název výbavy: ${eq.equipmentName}, `;
      if (eq.color) message += `Barva: ${eq.color}, `;
      if (eq.lights) message += `Světla: ${eq.lights}, `;
      if (eq.metalicColor !== undefined) message += `Metalický lak: ${toBoolText(eq.metalicColor)}, `;
      if (eq.wheelOrigin) message += `Originální kola: ${eq.wheelOrigin}, `;
      if (eq.wheelType) message += `Hliníková kola: ${eq.wheelType}, `;

      if (eq.carEquipment && eq.carEquipment.length > 0) {
        message += `Výbava: ${eq.carEquipment.join(', ')}, `;
      }

      if (eq.carEquipmentMessage) message += `Doplňující výbava: ${eq.carEquipmentMessage}, `;

      message = message.replace(/,\s$/, '\n\n');
    }

    if (data.carCondition) {
      const cond = data.carCondition;
      message += 'STAV VOZIDLA:\n';

      message += `Poškození vozidla: ${toBoolText(cond.carDamage)}, `;
      if (cond.carDamage === '1' && cond.carDamageMessage) {
        message += `Popis poškození: ${cond.carDamageMessage}, `;
      }

      message += `Mechanické poškození: ${toBoolText(cond.mechanicalDamage)}, `;
      if (cond.mechanicalDamage === '1' && cond.mechanicalDamageMessage) {
        message += `Popis mech. poškození: ${cond.mechanicalDamageMessage}, `;
      }

      message += `Poškození exteriéru: ${toBoolText(cond.exteriorDamage)}, `;
      if (cond.exteriorDamage === '1' && cond.exteriorDamageMessage) {
        message += `Popis poškození exteriéru: ${cond.exteriorDamageMessage}, `;
      }

      message += `Poškození interiéru: ${toBoolText(cond.interiorDamage)}, `;
      if (cond.interiorDamage === '1' && cond.interiorDamageMessage) {
        message += `Popis poškození interiéru: ${cond.interiorDamageMessage}, `;
      }

      message += `Servisní historie: ${cond.serviceHistory}, `;

      if (cond.comment) message += `Poznámka: ${cond.comment}, `;
      if (cond.urlLink) message += `Odkaz: ${cond.urlLink}, `;

      message = message.replace(/,\s$/, '\n\n');
    }

    if (data.carPhotos?.exterior && data.carPhotos.exterior.length > 0) {
      message += 'FOTOGRAFIE:\n';
      data.carPhotos.exterior.forEach((photo, index) => {
        const imageUrl = photo.data.startsWith('http') ? photo.data : `Fotografie ${index + 1}: ${photo.name}`;
        message += `${imageUrl}\n`;
      });
    }

    return message;
  }

  resetForm(): void {
    this.formId.set(null);
    this.globalsService.token.set('');
    this.globalsService.headerHashLogged.set({});
    this.globalsService.headerHashMD5.set({});
    this.carValuationData.set({} as CarFormData);
    this.step.set(1);
  }
}
